import Phaser from 'phaser';
import { GAME_HEIGHT, GAME_WIDTH } from '../config';
import {
  MAX_DICE_SLOTS,
  STARTING_UNLOCKED_DICE_SLOTS,
  computeCharacterCombatStats,
  createSeededRng,
  startDungeonRun,
  buildEquipmentContextFromLoadout,
  createEquipmentSnapshot,
} from '../../domain';
import {
  grantGeneralLevels,
  grantPointsToOpenSpecialisations,
  grantVanguardLevels,
  grantVanguardTalentPoints,
  loadDevPermanent,
  saveDevPermanent,
  snapshotDevProgress,
} from '../../persistence/debugProgress';
import {
  buildSavedGame,
  createSaveStore,
} from '../../persistence/saveStore';
import {
  grantDevDualWieldKitToPermanent,
  grantDevEquipmentToPermanent,
} from '../../persistence/equipmentFixtures';
import {
  createDefaultPermanentSave,
  inventoryFromPermanent,
} from '../../persistence/schema';
import {
  SAFE_TOP,
  addBackToVillageButton,
  addPrimaryButton,
  addSecondaryButton,
  titleBlock,
} from '../ui/hubUi';
import {
  addDungeonProgressionDevControls,
  type DungeonProgressionUiState,
} from '../ui/dungeonProgressionDevControls';

/**
 * Debug-only interface — starting dice, practice, permanent progression cheats.
 * Kept off the village hub so the home screen stays player-facing.
 * Prefer Settings → DEVELOPMENT for the same progression tools.
 *
 * Includes dungeon level + mutation overrides for testing (Gate players
 * cannot pick dungeon level).
 */
export class DebugMenuScene extends Phaser.Scene {
  private startingDice = STARTING_UNLOCKED_DICE_SLOTS;
  private startingDiceLabel?: Phaser.GameObjects.Text;
  private progressLabel?: Phaser.GameObjects.Text;
  private progression: DungeonProgressionUiState = {
    dungeonLevel: 1,
    mutation: 'standard',
  };

  constructor() {
    super('DebugMenu');
  }

  create(): void {
    const cx = GAME_WIDTH / 2;
    this.startingDice = STARTING_UNLOCKED_DICE_SLOTS;

    this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x0b1020).setOrigin(0);
    addBackToVillageButton(this);
    titleBlock(
      this,
      'DEV / DEBUG',
      'Testing overrides only. Caps match final Vanguard spec.',
      SAFE_TOP + 64,
    );

    this.progressLabel = this.add
      .text(cx, SAFE_TOP + 118, this.progressCaption(), {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '12px',
        color: '#c9a227',
        align: 'center',
        wordWrap: { width: GAME_WIDTH - 36 },
      })
      .setOrigin(0.5);

    this.renderStartingDiceControl(cx, 200);

    let y = 270;
    addSecondaryButton(this, cx, y, '+5 Vanguard talent pts', () => {
      this.applyProgress((p) => grantVanguardTalentPoints(p, 5));
    });
    y += 48;
    addSecondaryButton(this, cx, y, '+1 Vanguard class level', () => {
      this.applyProgress((p) => grantVanguardLevels(p, 1));
    });
    y += 48;
    addSecondaryButton(this, cx, y, '+5 Vanguard class levels', () => {
      this.applyProgress((p) => grantVanguardLevels(p, 5));
    });
    y += 48;
    addSecondaryButton(this, cx, y, '+1 General level', () => {
      this.applyProgress((p) => grantGeneralLevels(p, 1));
    });
    y += 48;
    addSecondaryButton(this, cx, y, 'Grant pts to open specs (15)', () => {
      this.applyProgress((p) => grantPointsToOpenSpecialisations(p));
    });
    y += 48;
    addSecondaryButton(this, cx, y, 'Add equipment to inventory (DEV)', () => {
      this.applyProgress((p) => grantDevEquipmentToPermanent(p).permanent);
    });
    y += 48;
    addSecondaryButton(this, cx, y, 'Add + equip dual-wield kit (DEV)', () => {
      this.applyProgress((p) => grantDevDualWieldKitToPermanent(p).permanent);
    });
    y += 52;

    addPrimaryButton(this, cx, y, 'Open Talents', () => {
      this.scene.start('Talents');
    });
    y += 52;

    const prog = addDungeonProgressionDevControls(this, cx, y, this.progression);
    this.progression = prog.state;
    y = prog.nextY + 8;

    addPrimaryButton(this, cx, y, 'Start dungeon (DEV dice)', () => {
      const store = createSaveStore();
      const previous = store.load();
      const permanent =
        previous?.permanent ??
        createDefaultPermanentSave({
          unlockedDiceSlots: this.startingDice,
        });
      const seed = Date.now() >>> 0;
      const rng = createSeededRng(seed);
      const classRanks = permanent.classes.vanguard.talentRanks ?? {};
      const inventory = inventoryFromPermanent(permanent);
      const equipmentSnapshot = createEquipmentSnapshot(inventory);
      const equipment = buildEquipmentContextFromLoadout(inventory, classRanks);
      const combatStats = computeCharacterCombatStats(
        classRanks,
        equipment,
        permanent.general.talentRanks ?? {},
        {
          classId: permanent.profile.activeClassId ?? 'vanguard',
          classLevel: permanent.classes.vanguard.level ?? 1,
        },
      );
      const dungeonRun = startDungeonRun(rng, seed, {
        unlockedDiceSlots: this.startingDice,
        combatStats,
        unlockedActiveSkillIds: combatStats.unlockedActiveSkillIds,
        playerMaxHp: combatStats.maxHp,
        equipmentSnapshot,
        dungeonLevel: this.progression.dungeonLevel,
        mutation: this.progression.mutation,
      });
      store.save(
        buildSavedGame(dungeonRun, rng.state, {
          permanent: {
            ...permanent,
            unlockedDiceSlots:
              permanent.unlockedDiceSlots === STARTING_UNLOCKED_DICE_SLOTS
                ? this.startingDice
                : permanent.unlockedDiceSlots,
          },
        }),
      );
      this.scene.start('Combat', {
        mode: 'dungeon',
        dungeonRun,
        rngState: rng.state,
      });
    });
    y += 52;
    addSecondaryButton(this, cx, y, 'Practice (3 dice)', () => {
      this.scene.start('Combat', {
        mode: 'practice',
        unlockedDiceSlots: 3,
        seed: Date.now() >>> 0,
        enemyTemplateIds: ['slime', 'goblin', 'bat'],
      });
    });
    y += 48;
    addSecondaryButton(this, cx, y, 'Clear local save', () => {
      createSaveStore().clear();
      this.scene.restart();
    });

    this.add
      .text(
        cx,
        GAME_HEIGHT - 36,
        'Also available under Settings → DEVELOPMENT',
        {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '11px',
          color: '#4a5168',
          align: 'center',
          wordWrap: { width: GAME_WIDTH - 40 },
        },
      )
      .setOrigin(0.5);
  }

  private progressCaption(): string {
    const { permanent } = loadDevPermanent();
    const s = snapshotDevProgress(permanent);
    return `Gen Lv ${s.generalLevel} · Vanguard Lv ${s.vanguardLevel} · pts ${s.unspentPoints} unspent (${s.earnedPoints} earned / ${s.spentPoints} spent)`;
  }

  private applyProgress(
    mutator: (permanent: ReturnType<typeof loadDevPermanent>['permanent']) =>
      ReturnType<typeof loadDevPermanent>['permanent'],
  ): void {
    const { permanent, previous } = loadDevPermanent();
    saveDevPermanent(mutator(permanent), previous);
    this.progressLabel?.setText(this.progressCaption());
  }

  private renderStartingDiceControl(cx: number, y: number): void {
    this.add
      .text(cx, y - 22, 'Starting dice (not permanent progression)', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '12px',
        color: '#b07a3a',
      })
      .setOrigin(0.5);

    const minus = this.add
      .rectangle(cx - 70, y + 10, 48, 40, 0x2a3350)
      .setStrokeStyle(1, 0xc9a227)
      .setInteractive({ useHandCursor: true });
    this.add
      .text(cx - 70, y + 10, '−', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '22px',
        color: '#e8d5a3',
      })
      .setOrigin(0.5);

    this.startingDiceLabel = this.add
      .text(cx, y + 10, this.startingDiceCaption(), {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '15px',
        color: '#f2e6c8',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    const plus = this.add
      .rectangle(cx + 70, y + 10, 48, 40, 0x2a3350)
      .setStrokeStyle(1, 0xc9a227)
      .setInteractive({ useHandCursor: true });
    this.add
      .text(cx + 70, y + 10, '+', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '22px',
        color: '#e8d5a3',
      })
      .setOrigin(0.5);

    minus.on('pointerup', () => this.adjustStartingDice(-1));
    plus.on('pointerup', () => this.adjustStartingDice(1));
  }

  private startingDiceCaption(): string {
    const n = this.startingDice;
    const def = n === STARTING_UNLOCKED_DICE_SLOTS ? ' · default' : '';
    return `${n} die${n === 1 ? '' : 's'}${def}`;
  }

  private adjustStartingDice(delta: number): void {
    this.startingDice = Math.min(
      MAX_DICE_SLOTS,
      Math.max(1, this.startingDice + delta),
    );
    this.startingDiceLabel?.setText(this.startingDiceCaption());
  }
}
